<template>
  <!-- A flow diagram: the records the assistant works with, wired into it. When the diagram
       scrolls into view the wires draw in, the nodes rise one by one and a pulse runs along each
       wire to the centre. Resting styles are the final frame (no JS, crawlers, reduce-motion).
       The node list and verbs follow the shared tool set of Ask AI and the MCP server
       (Workspace369-API functions-mcp, the same tools for both). Other pages set the core
       (coreLabel, and coreLogo or coreIcon) and may put any number of nodes on each side; right-side nodes with
       `out` are fed by the core, so their pulse runs outwards. -->
  <section class="sfl">
    <div class="sfl-shell">
      <header class="sfl-heading">
        <div>
          <h2>{{ chapter.title }}</h2>
        </div>
        <p class="sfl-intro">{{ chapter.intro }}</p>
      </header>

      <div
        ref="rootEl"
        class="sfl-map"
        :class="{ 'is-run': running || armed, 'is-armed': armed, 'sfl-map--io': hasLabels }"
        :style="{ '--sfl-h': `${height}px` }"
      >
        <svg class="sfl-wires" :viewBox="`0 0 1000 ${height}`" preserveAspectRatio="none" aria-hidden="true">
          <path
            v-for="(node, i) in placed"
            :key="node.label"
            class="sfl-wire"
            :d="wire(node)"
            pathLength="1"
            :style="{ '--d': `${0.15 + i * 0.07}s` }"
          />
          <circle
            v-for="(node, i) in placed"
            :key="`${node.label}-pulse`"
            class="sfl-pulse"
            r="4"
            :style="{ offsetPath: `path('${wire(node)}')`, '--d': `${0.9 + i * 0.12}s` }"
          />
        </svg>

        <div class="sfl-core">
          <span class="sfl-core-icon" :class="{ 'sfl-core-icon--logo': chapter.coreLogo }">
            <Logo v-if="chapter.coreLogo" class="sfl-core-logo" />
            <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path :d="chapter.coreIcon || sparkle" /></svg>
          </span>
          <strong translate="no">{{ chapter.coreLabel || "AI AutoPilot" }}</strong>
          <small>{{ chapter.coreCaption }}</small>
        </div>

        <template v-for="side in ['left', 'right']" :key="side">
          <p v-if="hasLabels" class="sfl-side-label" :class="`sfl-side-label--${side}`">
            {{ side === "left" ? chapter.leftLabel : chapter.rightLabel }}
          </p>
          <ul class="sfl-nodes" :class="`sfl-nodes--${side}`">
            <li
              v-for="node in placed.filter((n) => n.side === side)"
              :key="node.label"
              class="sfl-node"
              :class="`sfl-node--${side}`"
              :style="{ '--y': `${(node.y / height) * 100}%`, '--d': `${0.1 + node.i * 0.07}s` }"
            >
              <span class="sfl-node-icon" :style="{ '--tone': node.tone }"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="node.icon" /></svg></span>
              <span class="sfl-node-copy">
                <strong>{{ node.label }}</strong>
                <small>{{ node.copy }}</small>
              </span>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  chapter: { type: Object, required: true },
});

const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.3 });

const sparkle = "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z";
const height = computed(() => props.chapter.height || 460);
const hasLabels = computed(() => Boolean(props.chapter.leftLabel || props.chapter.rightLabel));
// Nodes without a `side` go half down each side (the first half on the left), in the order given. Four nodes on the default
// 460 map keep the rows the AI chat map was drawn with; any other side spreads its nodes evenly.
const rows = [62, 172, 288, 398];
const placed = computed(() => {
  const half = Math.ceil(props.chapter.nodes.length / 2);
  const sided = props.chapter.nodes.map((node, i) => ({ ...node, i, side: node.side || (i < half ? "left" : "right") }));
  const top = hasLabels.value ? 36 : 0;
  return sided.map((node) => {
    const group = sided.filter((n) => n.side === node.side);
    const index = group.indexOf(node);
    if (group.length === 4 && height.value === 460 && !top) return { ...node, y: rows[index] };
    const slot = (height.value - top) / group.length;
    return { ...node, y: Math.round(top + slot * (index + 0.5)) };
  });
});
// Nodes take the outer 30% on each side and the core the middle 20% (see the CSS), so in the
// 1000-unit viewBox a wire runs from a node's inner edge (300 or 700) to the core's side (400 or
// 600). An `out` wire starts at the core, so its pulse travels to the node.
const wire = (node) => {
  const nodeX = node.side === "left" ? 300 : 700;
  const coreX = node.side === "left" ? 400 : 600;
  const mid = (nodeX + coreX) / 2;
  const coreY = height.value / 2;
  return node.out
    ? `M ${coreX} ${coreY} C ${mid} ${coreY}, ${mid} ${node.y}, ${nodeX} ${node.y}`
    : `M ${nodeX} ${node.y} C ${mid} ${node.y}, ${mid} ${coreY}, ${coreX} ${coreY}`;
};
</script>

<style scoped>
.sfl { background: #f8fafc; padding: 6.5rem 0; }
.dark .sfl { background: #0b1220; }
.sfl-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.sfl-heading { display: grid; gap: 1.5rem; align-items: end; }
.sfl-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .sfl-heading h2 { color: #f8fafc; }
.sfl-intro { color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .sfl-intro { color: #cbd5e1; }
@media (min-width: 1024px) { .sfl-heading { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }

.sfl-map { position: relative; height: var(--sfl-h, 460px); margin-top: 3rem; }
.sfl-side-label {
  position: absolute;
  top: 0;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.dark .sfl-side-label { color: #94a3b8; }
.sfl-side-label--left { left: 0; }
.sfl-side-label--right { right: 0; width: 30%; }
.sfl-wires { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.sfl-wire { fill: none; stroke: #94a3b8; stroke-width: 1.5; stroke-dasharray: 1; stroke-dashoffset: 0; }
.dark .sfl-wire { stroke: #475569; }
.sfl-pulse { fill: #1ba7ce; opacity: 0; offset-distance: 100%; }

.sfl-core {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  justify-items: center;
  gap: 0.35rem;
  width: 20%;
  transform: translate(-50%, -50%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 1.25rem 1rem;
  text-align: center;
  box-shadow: 0 18px 40px -20px rgb(15 23 42 / 0.35);
}
.dark .sfl-core { border-color: #1e293b; background: #0f172a; }
.sfl-core-icon { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 9px; background: #0f172a; color: #67e8f9; }
.dark .sfl-core-icon { background: #e2e8f0; color: #0e7490; }
.sfl-core-icon svg { width: 24px; height: 24px; fill: currentColor; }
.sfl-core-icon--logo, .dark .sfl-core-icon--logo { background: transparent; color: inherit; }
.sfl-core-logo { width: 44px; height: 44px; }
.sfl-core strong { color: #0f172a; font-size: 1rem; font-weight: 800; }
.dark .sfl-core strong { color: #f8fafc; }
.sfl-core small { color: #64748b; font-size: 0.78rem; line-height: 1.4; }
.dark .sfl-core small { color: #94a3b8; }

.sfl-nodes { margin: 0; padding: 0; list-style: none; }
.sfl-node {
  position: absolute;
  top: var(--y);
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 30%;
  transform: translateY(-50%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 0.65rem 0.8rem;
}
.sfl-node--left { left: 0; }
.sfl-node--right { right: 0; }
.dark .sfl-node { border-color: #1e293b; background: #0f172a; }
.sfl-node-icon { display: grid; place-items: center; flex: none; width: 32px; height: 32px; border-radius: 7px; background: color-mix(in srgb, var(--tone) 14%, transparent); color: var(--tone); }
.sfl-node-icon svg { width: 18px; height: 18px; fill: currentColor; }
.sfl-node-copy { display: grid; min-width: 0; line-height: 1.3; }
.sfl-node-copy strong { color: #0f172a; font-size: 0.92rem; font-weight: 700; }
.dark .sfl-node-copy strong { color: #f1f5f9; }
.sfl-node-copy small { color: #64748b; font-size: 0.8rem; }
.dark .sfl-node-copy small { color: #94a3b8; }

/* Motion: wires draw, nodes rise, then a pulse runs into the core. */
.is-run .sfl-wire { animation: sfl-draw 0.9s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .sfl-node { animation: sfl-rise 0.6s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .sfl-core { animation: sfl-core 0.7s cubic-bezier(0.33, 1, 0.68, 1) both; }
.is-run .sfl-pulse { animation: sfl-pulse 1.3s ease-in-out var(--d) 2 both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sfl-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes sfl-rise { from { opacity: 0; transform: translateY(calc(-50% + 12px)); } to { opacity: 1; transform: translateY(-50%); } }
@keyframes sfl-core { from { opacity: 0; transform: translate(-50%, -50%) scale(0.94); } to { opacity: 1; transform: translate(-50%, -50%); } }
@keyframes sfl-pulse { 0% { opacity: 0; offset-distance: 0%; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; offset-distance: 100%; } }

/* Tablets: narrower nodes. Phones: the core on top and the nodes in a grid, no wires. */
@media (max-width: 1180px) {
  .sfl-node-copy small { display: none; }
}
@media (max-width: 860px) {
  .sfl { padding: 4.5rem 0; }
  .sfl-map { display: grid; gap: 0.75rem; height: auto; }
  .sfl-wires { display: none; }
  .sfl-core { position: static; order: -1; width: auto; transform: none; grid-template-columns: auto 1fr; justify-items: start; text-align: left; column-gap: 0.85rem; }
  /* In and out: calls in, then the core, then events out, each side under its own label. */
  .sfl-map--io .sfl-core { order: 0; }
  .sfl-map--io .sfl-side-label--left, .sfl-map--io .sfl-nodes--left { order: -1; }
  .sfl-side-label { position: static; width: auto; margin-top: 0.5rem; }
  .sfl-side-label--right { order: 1; }
  .sfl-nodes--right { order: 2; }
  .sfl-core-icon { grid-row: span 2; }
  .sfl-nodes { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; }
  .sfl-node { position: static; width: auto; transform: none; }
  .sfl-node-copy small { display: block; }
  @keyframes sfl-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  @keyframes sfl-core { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: none; } }
}
@media (max-width: 520px) {
  .sfl-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
  .sfl-nodes { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .sfl-map * { animation: none !important; }
}
</style>
